import {
	ReactElement,
	useCallback
} from "react";
import styles from "./StepList.module.less";
import {
	useConfirmationDialog
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import {
	IObjectInputAttachable,
	IValueInputAttachable
} from "client/Forms/attachable";
import {
	getClientId,
	ObjectWithClientId
} from "client/Misc/clientId";
import { Guid } from "client_legacy/ndaw/guid";
import {
	IntegrationFlowConfigurationValidateResponseModel,
	IntegrationFlowRequestResultModel,
	StepKind
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import {
	getRequestDescription,
	getRequestStepTag,
	getResultMapperStepStatus,
	statusToTagConfigurationMap,
	StepConfiguration,
	StepStatus
} from "./common";
import { Step } from "./Step";
import { never } from "client_legacy/ndaw/errors";

export type StepListProps = {
	attachable: IValueInputAttachable<readonly ObjectWithClientId<StepConfiguration>[]>;
	stepResults: Map<Guid, IntegrationFlowRequestResultModel>;
	isReadOnly: boolean;
	stepWithErrorIds: ReadonlySet<Guid>;
	validateResponse: IntegrationFlowConfigurationValidateResponseModel | null;
	onOpenStep: (clientId: Guid) => void;
	onStepDeleted(): void;
};

export const StepList = (props: StepListProps): ReactElement => {
	const {
		attachable,
		stepResults,
		isReadOnly,
		stepWithErrorIds,
		validateResponse,
		onOpenStep,
		onStepDeleted
	} = props;

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

	const hasAnyNoneMappingSteps: boolean = attachable
		.value
		.filter(
			x => x.kind !== StepKind.Mapping
		)
		.length > 0;

	const deleteHandler = useCallback(
		async (index: number): Promise<void> => {
			const result = await openConfirmationDialog({
				title: localize("_DeleteThisItem"),
				confirmText: localize("_Delete"),
				trackingId: "delete-step-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			onStepDeleted();

			attachable.reducer
				.removeAt(index);

			attachable.validation
				.triggerInputFinished();
		},
		[
			attachable.reducer,
			attachable.validation,
			openConfirmationDialog,
			onStepDeleted
		]
	);

	const renderStep = (
		stepHandler: IObjectInputAttachable<ObjectWithClientId<StepConfiguration>>,
		index: number
	): ReactElement => {
		const { value } = stepHandler;
		const clientId: Guid = getClientId(value);

		switch(value.kind) {
			case StepKind.Mapping:
				const stepStatus: StepStatus | "unknown" = getResultMapperStepStatus(
					value,
					validateResponse,
					stepWithErrorIds
				);

				return (
					<Step
						title={localize("_ResultMapper")}
						description={localize("_ResultMapperDescription")}
						onOpen={() => onOpenStep(clientId)}
						isDisabled={!hasAnyNoneMappingSteps}
						trackingId="map-step"
						tagConfiguration={statusToTagConfigurationMap[stepStatus]}
					/>
				);
			case StepKind.Request:
			case StepKind.PerItemRequest:
				const hasError: boolean = stepWithErrorIds.has(clientId);

				return (
					<Step
						title={value.name}
						description={getRequestDescription(value)}
						onDelete={!isReadOnly
							? () => deleteHandler(index)
							: undefined
						}
						onOpen={() => onOpenStep(clientId)}
						trackingId={generateTrackingId("request-step", `|${index}`)}
						tagConfiguration={getRequestStepTag(hasError, clientId, stepResults)}
					/>
				);
			default:
				never(value);
		}
	};

	return (
		<>
			<ul
				{...setTrackingId("steps")}
				className={styles.root}
			>
				{attachable.map((x, i): ReactElement => {
					const clientId: Guid = getClientId(x.value);

					return (
						<li key={clientId}>
							{renderStep(x, i)}
						</li>
					);
				})}
			</ul>
			{confirmationDialog}
		</>
	);
};
StepList.displayName = "StepList";
